/**
 * Strips trailing serving-style tokens from a beer name (ITT-946).
 *
 * Keep in lockstep with backend `functions/src/domain/beer/stripServingStyleSuffix.ts`.
 * There is no exception list: `Rough Draft` becomes `Rough`.
 */

const MAX_STRIP_PASSES = 8;
const TOKEN_GAP = '[\\s\\-\\u2013\\u2014]+';
const TRAILING_SEPARATORS = /[\s\-–—|/]+$/u;

const SERVING_STYLE_SUFFIXES = [
  'bottle pour',
  'can pour',
  'draft pour',
  'tap pour',
  'on tap',
  'to go',
  '4 pack',
  'crowler',
  'growler',
  'nitro',
  'flight',
  'draft',
  'can',
  'bottle',
  'keg',
  'tap',
];

const SORTED_SERVING_STYLE_SUFFIXES = [...SERVING_STYLE_SUFFIXES].sort(
  (left, right) => right.length - left.length,
);

const SERVING_STYLE_SUFFIX_PATTERNS = SORTED_SERVING_STYLE_SUFFIXES.map(
  (token) => new RegExp(buildServingStyleSuffixPattern(token), 'iu'),
);

/**
 * Builds a trailing-suffix regex for one allowlisted token.
 *
 * @param {string} token - Space-separated canonical token (`to go`, `draft`)
 * @returns {string} Pattern source anchored at the end of the name
 */
function buildServingStyleSuffixPattern(token) {
  const escapedParts = token.split(' ').map((part) => escapeRegExp(part));
  const tokenSource = escapedParts.join(TOKEN_GAP);
  const prefix = '(?:\\s*[\\-\\u2013\\u2014|/]\\s*|\\s*[(\\[]\\s*|\\s+)';
  return `${prefix}${tokenSource}[)\\]]?[^\\p{L}\\p{N}]*$`;
}

/**
 * Escapes a token part for use in a regular expression.
 *
 * @param {string} value - Literal token fragment
 * @returns {string} Escaped fragment
 */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Removes one trailing serving-style suffix when a remainder would remain.
 *
 * @param {string} name - Trimmed beer name
 * @returns {string|null} Name without the suffix, or null when nothing was removed
 */
function stripOneServingStyleSuffix(name) {
  for (const pattern of SERVING_STYLE_SUFFIX_PATTERNS) {
    const withoutMatch = name.replace(pattern, '');
    if (withoutMatch === name) {
      continue;
    }
    const withoutSuffix = withoutMatch.replace(TRAILING_SEPARATORS, '').trim();
    if (withoutSuffix === '' || withoutSuffix === name) {
      continue;
    }
    return withoutSuffix;
  }
  return null;
}

/**
 * Strips trailing serving-style labels from a beer display name.
 *
 * @param {string} name - Raw or trimmed beer name
 * @returns {string} Remaining name; empty string when the input is blank
 */
export function stripServingStyleSuffix(name) {
  const original = typeof name === 'string' ? name.trim() : '';
  if (original === '') {
    return '';
  }

  let remaining = original;
  for (let pass = 0; pass < MAX_STRIP_PASSES; pass += 1) {
    const stripped = stripOneServingStyleSuffix(remaining);
    if (stripped === null) {
      break;
    }
    remaining = stripped;
  }
  return remaining;
}
